import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  useMemberUpcomingEvents,
  useMemberInvitations,
  formatEventWhen,
} from "../hooks/useDbEvents";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

export default function MemberDashboard() {
  const { session } = useAuth();
  const uid = session?.user?.id;
  const { events, loading, error, refresh } = useMemberUpcomingEvents();
  const { invitations, loading: invLoading, refresh: refreshInv } =
    useMemberInvitations(uid);
  const [busyId, setBusyId] = useState(null);

  const setStatus = async (invitationId, status) => {
    if (!supabase) return;
    setBusyId(invitationId);
    await supabase
      .from("event_invitations")
      .update({ status })
      .eq("id", invitationId);
    setBusyId(null);
    refreshInv();
  };

  if (!isSupabaseConfigured) {
    return <p className="text-danger">Supabase is not configured.</p>;
  }

  return (
    <div>
      <h1 className="h3 mb-3">Upcoming for you</h1>
      <p className="text-muted small">
        Includes <strong>public</strong> and <strong>members-only</strong> events, and anything you were{" "}
        <strong>invited</strong> to.
      </p>

      {error && <div className="alert alert-warning small">{error}</div>}

      <h2 className="h5 mt-4">Invitations</h2>
      {invLoading ? (
        <p className="text-muted small">Loading…</p>
      ) : invitations.length === 0 ? (
        <p className="text-muted small">No upcoming invitations.</p>
      ) : (
        <div className="list-group mb-4">
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="list-group-item d-flex flex-column flex-md-row justify-content-between gap-2"
            >
              <div>
                <Link to={`/events/${inv.event?.id}`} className="fw-bold">
                  {inv.event?.title || "Event"}
                </Link>
                <div className="small text-muted">
                  {inv.event
                    ? formatEventWhen(inv.event.starts_at, inv.event.ends_at)
                    : ""}{" "}
                  · Status: <strong>{inv.status}</strong>
                </div>
              </div>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-success"
                  disabled={busyId === inv.id || inv.status === "accepted"}
                  onClick={() => setStatus(inv.id, "accepted")}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={busyId === inv.id || inv.status === "maybe"}
                  onClick={() => setStatus(inv.id, "maybe")}
                >
                  Maybe
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={busyId === inv.id || inv.status === "declined"}
                  onClick={() => setStatus(inv.id, "declined")}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <h2 className="h5 mb-0">All upcoming you can access</h2>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={refresh}>
          Refresh
        </button>
      </div>
      {loading ? (
        <p className="text-muted small mt-2">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-muted small mt-2">No upcoming events.</p>
      ) : (
        <ul className="list-group mt-2">
          {events.map((ev) => (
            <li key={ev.id} className="list-group-item">
              <Link to={`/events/${ev.id}`} className="fw-bold">
                {ev.title}
              </Link>
              <div className="small text-muted">
                {formatEventWhen(ev.starts_at, ev.ends_at)} ·{" "}
                <span className="badge bg-light text-dark border">{ev.visibility}</span>
                {ev.location ? ` · ${ev.location}` : ""}
              </div>
              {ev.summary && <p className="small mb-0 mt-1">{ev.summary}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
